import { ethers } from "ethers";
import {
  recoveryPhraseToWalletMaterial,
  spendingKeyToRecoveryPhrase,
  type RecoveryWalletMaterial,
} from "./recoveryPhrase";
import {
  encodeOnchainRecoveryContext,
  MAX_ONCHAIN_RECOVERY_BYTES,
  ONCHAIN_RECOVERY_VERSION,
  OnchainRecoveryError,
  recoveryBuffer,
  recoveryWord,
  type OnchainRecoveryContext,
} from "./onchainRecoveryCodec";

export interface MasterRecoveryNetwork {
  chain: "evm" | "solana";
  chainDomain: string;
}

/** A fresh global wallet uses random entropy, never a chain-specific signature. */
export interface MasterRecoveryWallet {
  version: 1;
  rootKey: Uint8Array;
  recoveryEntropy: Uint8Array;
}

export interface MasterRecoveryEntry {
  network: MasterRecoveryNetwork;
  /** Exact original key; importing it never rederives or rewrites existing notes. */
  spendingKey: Uint8Array;
  /** Explicitly retains the previous receiving identity on this network. */
  primary: boolean;
}

export interface MasterRecoveryBundle {
  version: 1;
  revision: number;
  entries: MasterRecoveryEntry[];
}

export const MAX_MASTER_RECOVERY_ENTRIES = 29;
const ENTRY_BYTES = 66;
const BUNDLE_HEADER_BYTES = 6;
const SEALED_HEADER_BYTES = 14;
const KEY_DOMAIN = "StealthOS master recovery private-key bundle v1";
const NETWORK_DOMAIN = "StealthOS master recovery network spending key v1";

function invalid(): never {
  throw new OnchainRecoveryError("Invalid master recovery data.");
}

function checkedKey(key: Uint8Array): Uint8Array {
  if (
    !(key instanceof Uint8Array) ||
    key.length !== 32 ||
    key.every((byte) => byte === 0)
  )
    return invalid();
  return new Uint8Array(key);
}

function networkBytes(network: MasterRecoveryNetwork): Uint8Array {
  if (!network || !["evm", "solana"].includes(network.chain)) return invalid();
  return ethers.getBytes(
    ethers.concat([
      new Uint8Array([network.chain === "evm" ? 1 : 2]),
      recoveryWord(network.chainDomain),
    ]),
  );
}

function networkId(network: MasterRecoveryNetwork): string {
  return ethers.hexlify(networkBytes(network));
}

function canonicalNetwork(
  network: MasterRecoveryNetwork,
): MasterRecoveryNetwork {
  networkBytes(network);
  return {
    chain: network.chain,
    chainDomain: network.chainDomain.toLowerCase(),
  };
}

export function createMasterRecoveryWallet(): MasterRecoveryWallet {
  let entropy: Uint8Array;
  do {
    entropy = crypto.getRandomValues(new Uint8Array(16));
  } while (entropy.every((byte) => byte === 0));
  const phrase = ethers.Mnemonic.fromEntropy(
    entropy,
    "",
    ethers.wordlists.en,
  ).phrase;
  return masterRecoveryWalletFromPhrase(phrase);
}

/**
 * Keeps the deployed 12-word mapping byte-for-byte. Global-wallet mode is an
 * explicit caller choice; words alone cannot distinguish an old chain wallet.
 * Old 24-word keys are imported into a bundle, not converted into new entropy.
 */
export function masterRecoveryWalletFromPhrase(
  phrase: string,
): MasterRecoveryWallet {
  const material = recoveryPhraseToWalletMaterial(phrase);
  if (!material.recoveryEntropy) return invalid();
  return {
    version: 1,
    rootKey: new Uint8Array(material.spendingKey),
    recoveryEntropy: new Uint8Array(material.recoveryEntropy),
  };
}

export function masterRecoveryWalletToPhrase(
  wallet: MasterRecoveryWallet,
): string {
  if (!wallet || wallet.version !== 1) return invalid();
  return spendingKeyToRecoveryPhrase(wallet.rootKey, wallet.recoveryEntropy);
}

/** Stable network domain: pool upgrades and a different public wallet do not change this key. */
export async function deriveMasterNetworkKey(
  rootKey: Uint8Array,
  network: MasterRecoveryNetwork,
): Promise<Uint8Array> {
  const root = checkedKey(rootKey);
  try {
    return ethers.getBytes(
      ethers.sha256(
        ethers.concat([
          ethers.toUtf8Bytes(NETWORK_DOMAIN),
          root,
          networkBytes(network),
        ]),
      ),
    );
  } finally {
    root.fill(0);
  }
}

function canonicalBundle(bundle: MasterRecoveryBundle): MasterRecoveryBundle {
  if (
    !bundle ||
    bundle.version !== 1 ||
    !Number.isSafeInteger(bundle.revision) ||
    bundle.revision < 0 ||
    bundle.revision > 0xffffffff ||
    !Array.isArray(bundle.entries) ||
    bundle.entries.length > MAX_MASTER_RECOVERY_ENTRIES
  )
    return invalid();
  const identities = new Set<string>();
  const primaries = new Set<string>();
  const entries = bundle.entries.map((entry) => {
    if (!entry || typeof entry.primary !== "boolean") return invalid();
    const network = canonicalNetwork(entry.network);
    const spendingKey = checkedKey(entry.spendingKey);
    const id = networkId(network);
    const identity = `${id}:${ethers.hexlify(spendingKey)}`;
    if (identities.has(identity)) return invalid();
    identities.add(identity);
    if (entry.primary) {
      if (primaries.has(id)) return invalid();
      primaries.add(id);
    }
    return { network, spendingKey, primary: entry.primary };
  });
  entries.sort((left, right) => {
    const a = `${networkId(left.network)}:${ethers.hexlify(left.spendingKey)}`;
    const b = `${networkId(right.network)}:${ethers.hexlify(right.spendingKey)}`;
    return a < b ? -1 : a > b ? 1 : 0;
  });
  return { version: 1, revision: bundle.revision, entries };
}

/** Explicit import only; primary replacement preserves the previous key as a legacy entry. */
export function importLegacyRecoveryWallet(
  bundle: MasterRecoveryBundle,
  network: MasterRecoveryNetwork,
  material: RecoveryWalletMaterial,
  options: { makePrimary?: boolean } = {},
): MasterRecoveryBundle {
  const old = canonicalBundle(bundle);
  const normalizedNetwork = canonicalNetwork(network);
  const spendingKey = checkedKey(material.spendingKey);
  if (material.recoveryEntropy) {
    // Reject a mismatched entropy/key pair, preserving the legacy mapping.
    spendingKeyToRecoveryPhrase(spendingKey, material.recoveryEntropy);
  }
  const id = networkId(normalizedNetwork);
  let found = false;
  const entries = old.entries.map((entry) => {
    if (networkId(entry.network) !== id) return entry;
    if (ethers.hexlify(entry.spendingKey) === ethers.hexlify(spendingKey)) {
      found = true;
      return { ...entry, primary: options.makePrimary || entry.primary };
    }
    return { ...entry, primary: options.makePrimary ? false : entry.primary };
  });
  if (!found)
    entries.push({
      network: normalizedNetwork,
      spendingKey,
      primary: !!options.makePrimary,
    });
  return canonicalBundle({ version: 1, revision: old.revision + 1, entries });
}

export function importLegacyRecoveryPhrase(
  bundle: MasterRecoveryBundle,
  network: MasterRecoveryNetwork,
  phrase: string,
  options: { makePrimary?: boolean } = {},
): MasterRecoveryBundle {
  return importLegacyRecoveryWallet(
    bundle,
    network,
    recoveryPhraseToWalletMaterial(phrase),
    options,
  );
}

export async function resolveMasterRecoveryNetwork(
  rootKey: Uint8Array,
  network: MasterRecoveryNetwork,
  bundle: MasterRecoveryBundle = { version: 1, revision: 0, entries: [] },
): Promise<{ primary: Uint8Array; legacy: Uint8Array[] }> {
  const derived = await deriveMasterNetworkKey(rootKey, network);
  const entries = canonicalBundle(bundle).entries.filter(
    (entry) => networkId(entry.network) === networkId(network),
  );
  const primary =
    entries.find((entry) => entry.primary)?.spendingKey ?? derived;
  const byKey = new Map<string, Uint8Array>();
  for (const key of [derived, ...entries.map((entry) => entry.spendingKey)])
    if (ethers.hexlify(key) !== ethers.hexlify(primary))
      byKey.set(ethers.hexlify(key), new Uint8Array(key));
  return { primary: new Uint8Array(primary), legacy: [...byKey.values()] };
}

/** Accumulates old keys rather than allowing a newer bundle to erase access. */
export function mergeMasterRecoveryBundles(
  bundles: readonly MasterRecoveryBundle[],
): MasterRecoveryBundle {
  const result = new Map<string, MasterRecoveryEntry>();
  const selected = new Map<string, { revision: number; key: string }>();
  let revision = 0;
  for (const raw of bundles) {
    const bundle = canonicalBundle(raw);
    revision = Math.max(revision, bundle.revision);
    for (const entry of bundle.entries) {
      const network = networkId(entry.network);
      const key = ethers.hexlify(entry.spendingKey);
      result.set(`${network}:${key}`, { ...entry, primary: false });
      if (!entry.primary) continue;
      const previous = selected.get(network);
      if (previous?.revision === bundle.revision && previous.key !== key)
        throw new OnchainRecoveryError(
          "Conflicting master recovery identities; choose the intended wallet explicitly.",
        );
      if (!previous || previous.revision < bundle.revision)
        selected.set(network, { revision: bundle.revision, key });
    }
  }
  for (const [network, choice] of selected) {
    const entry = result.get(`${network}:${choice.key}`);
    if (entry) entry.primary = true;
  }
  return canonicalBundle({
    version: 1,
    revision,
    entries: [...result.values()],
  });
}

function encodeBundle(bundle: MasterRecoveryBundle): Uint8Array {
  const canonical = canonicalBundle(bundle);
  const bytes = new Uint8Array(
    BUNDLE_HEADER_BYTES + ENTRY_BYTES * canonical.entries.length,
  );
  bytes[0] = 1;
  new DataView(bytes.buffer).setUint32(1, canonical.revision, false);
  bytes[5] = canonical.entries.length;
  canonical.entries.forEach((entry, index) => {
    const offset = BUNDLE_HEADER_BYTES + index * ENTRY_BYTES;
    bytes.set(networkBytes(entry.network), offset);
    bytes[offset + 33] = entry.primary ? 1 : 0;
    bytes.set(entry.spendingKey, offset + 34);
    entry.spendingKey.fill(0);
  });
  return bytes;
}

function decodeBundle(bytes: Uint8Array): MasterRecoveryBundle {
  if (
    bytes.length < BUNDLE_HEADER_BYTES ||
    bytes[0] !== 1 ||
    bytes[5] > MAX_MASTER_RECOVERY_ENTRIES ||
    bytes.length !== BUNDLE_HEADER_BYTES + bytes[5] * ENTRY_BYTES
  )
    return invalid();
  const entries: MasterRecoveryEntry[] = [];
  for (let index = 0; index < bytes[5]; index += 1) {
    const offset = BUNDLE_HEADER_BYTES + index * ENTRY_BYTES;
    if (![1, 2].includes(bytes[offset]) || ![0, 1].includes(bytes[offset + 33]))
      return invalid();
    entries.push({
      network: {
        chain: bytes[offset] === 1 ? "evm" : "solana",
        chainDomain: ethers.hexlify(bytes.slice(offset + 1, offset + 33)),
      },
      spendingKey: bytes.slice(offset + 34, offset + 66),
      primary: bytes[offset + 33] === 1,
    });
  }
  return canonicalBundle({
    version: 1,
    revision: new DataView(
      bytes.buffer,
      bytes.byteOffset,
      bytes.byteLength,
    ).getUint32(1, false),
    entries,
  });
}

async function bundleKey(
  rootKey: Uint8Array,
  usage: KeyUsage,
): Promise<CryptoKey> {
  const root = checkedKey(rootKey);
  try {
    const material = await crypto.subtle.importKey(
      "raw",
      recoveryBuffer(root),
      "HKDF",
      false,
      ["deriveKey"],
    );
    return await crypto.subtle.deriveKey(
      {
        name: "HKDF",
        hash: "SHA-256",
        salt: recoveryBuffer(ethers.toUtf8Bytes(KEY_DOMAIN)),
        info: recoveryBuffer(
          ethers.toUtf8Bytes("root-spending-authority-only"),
        ),
      },
      material,
      { name: "AES-GCM", length: 256 },
      false,
      [usage],
    );
  } finally {
    root.fill(0);
  }
}

/** NEVER substitute a viewing key for rootKey: this payload contains spend authority. */
export async function sealMasterRecoveryBundle(
  rootKey: Uint8Array,
  bundle: MasterRecoveryBundle,
  context: OnchainRecoveryContext,
): Promise<Uint8Array> {
  if (context.kind !== 3) return invalid();
  const aad = encodeOnchainRecoveryContext(context);
  const plaintext = encodeBundle(bundle);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  try {
    const key = await bundleKey(rootKey, "encrypt");
    const encrypted = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: recoveryBuffer(nonce),
        additionalData: recoveryBuffer(aad),
        tagLength: 128,
      },
      key,
      recoveryBuffer(plaintext),
    );
    const result = ethers.getBytes(
      ethers.concat([
        new Uint8Array([ONCHAIN_RECOVERY_VERSION, 3]),
        nonce,
        new Uint8Array(encrypted),
      ]),
    );
    if (result.length > MAX_ONCHAIN_RECOVERY_BYTES) return invalid();
    return result;
  } catch {
    return invalid();
  } finally {
    plaintext.fill(0);
  }
}

export async function openMasterRecoveryBundle(
  rootKey: Uint8Array,
  envelope: Uint8Array,
  context: OnchainRecoveryContext,
): Promise<MasterRecoveryBundle> {
  if (
    context.kind !== 3 ||
    !(envelope instanceof Uint8Array) ||
    envelope.length < SEALED_HEADER_BYTES + 16 + BUNDLE_HEADER_BYTES ||
    envelope.length > MAX_ONCHAIN_RECOVERY_BYTES ||
    envelope[0] !== ONCHAIN_RECOVERY_VERSION ||
    envelope[1] !== 3
  )
    return invalid();
  const aad = encodeOnchainRecoveryContext(context);
  try {
    const key = await bundleKey(rootKey, "decrypt");
    const plaintext = new Uint8Array(
      await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: recoveryBuffer(envelope.slice(2, 14)),
          additionalData: recoveryBuffer(aad),
          tagLength: 128,
        },
        key,
        recoveryBuffer(envelope.slice(SEALED_HEADER_BYTES)),
      ),
    );
    try {
      return decodeBundle(plaintext);
    } finally {
      plaintext.fill(0);
    }
  } catch {
    return invalid();
  }
}
